import { useState, type FormEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts";
import { Button } from "../Button";
import { AuthInput } from "./AuthInput";

interface LoginFormProps {
  onSuccess?: () => void;
}

/**
 * LoginForm - Email/password login form with Supabase integration.
 * Displays friendly error messages and handles loading states.
 */
export function LoginForm({ onSuccess }: LoginFormProps) {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the redirect path from location state (set by AuthGuard)
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/profiles";

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!email) {
      newErrors.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Please enter your password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) return;

    setIsSubmitting(true);

    const { error } = await login(email, password);

    setIsSubmitting(false);

    if (error) {
      // Map Supabase error messages to user-friendly messages
      let friendlyMessage = "Something went wrong. Please try again.";

      if (error.message?.includes("Invalid login credentials")) {
        friendlyMessage = "Email or password is incorrect. Please try again.";
      } else if (error.message?.includes("Email not confirmed")) {
        friendlyMessage = "Please check your email and confirm your account first.";
      } else if (error.message?.includes("Too many requests")) {
        friendlyMessage = "Too many login attempts. Please wait a moment and try again.";
      }

      setErrors({ form: friendlyMessage });
      return;
    }

    onSuccess?.();
    navigate(from, { replace: true });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* Form error message */}
      {errors.form && (
        <div
          className="
            p-4 rounded-xl
            bg-fairy-50 border-2 border-fairy-200
            text-fairy-700 font-body text-body-md
            flex items-start gap-3
          "
          role="alert"
        >
          <span className="text-xl flex-shrink-0">😕</span>
          <span>{errors.form}</span>
        </div>
      )}

      <AuthInput
        id="email"
        type="email"
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="parent@example.com"
        error={errors.email}
        autoComplete="email"
        autoFocus
        disabled={isSubmitting || isLoading}
      />

      <div>
        <AuthInput
          id="password"
          type="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          error={errors.password}
          autoComplete="current-password"
          disabled={isSubmitting || isLoading}
        />
        <div className="mt-2 text-right">
          <Link
            to="/forgot-password"
            className="
              font-body text-body-sm text-magic-600
              hover:text-magic-700
              transition-colors duration-200
              focus:outline-none focus-visible:underline
            "
          >
            Forgot password?
          </Link>
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        fullWidth
        size="lg"
        isLoading={isSubmitting || isLoading}
        disabled={isSubmitting || isLoading}
      >
        Log In
      </Button>

      <p className="font-body text-body-md text-neutral-600 text-center">
        Don't have an account?{" "}
        <Link
          to="/register"
          className="text-magic-600 font-semibold hover:text-magic-700 transition-colors"
        >
          Sign up free
        </Link>
      </p>
    </form>
  );
}

export default LoginForm;
